'use client';

import { HubStatus, type PublicHub } from '@api/models';
import { useMemo, useState } from 'react';

import type { HubOption, HubSearch } from '@ui/components/features/locationLibrary';
import { placementStanding } from '@website/utils/hubs';

import { useOwnProfessionalProfile } from './useCurrentProfessional';
import { useCurrentUser } from './useCurrentUser';
import { useHub, useHubSearch, useHubViewer } from './useHubs';

/** Why the wizard opened from a Hub page cannot place the activity there, said at once (`ApiErrors.<code>`). */
export type HubPlacementNotice = 'professional_not_approved' | 'hub_blocked';

/**
 * The Hub a Hub page's "Create an activity here" opens the wizard at, when it still takes activities
 * and its Managers did not block the professional (`blocked` undefined: not known yet, no preselection).
 */
export const toInitialHub = (hub: PublicHub | null | undefined, blocked: boolean | undefined): HubOption | null =>
  hub && blocked === false && hub.status === HubStatus.approved
    ? { id: hub.id, name: hub.name, addressLine1: hub.addressLine1, city: hub.city, timezone: hub.timezone }
    : null;

export interface WizardInitialHub {
  /** The Hub a Hub page opened the wizard at, when the professional may place the activity there. */
  initialHub: HubOption | null;
  /** Why it cannot, said at once. */
  notice: HubPlacementNotice | null;
  /** Only an approved professional places activities at a Hub: no "at a Hub" choice otherwise. */
  profileNotApproved: boolean;
}

/**
 * The Hub the wizard opens at (`?hubId=`, from a Hub page), or why it cannot: only an approved
 * professional whom the Hub's Managers did not block. The profile status always comes from the
 * professional's own profile, with or without a Hub; the Hub's viewer read only says whether they
 * are blocked (not known until it answers, or when it fails: no preselection, the save still
 * refuses a blocked placement). An admin editing someone's activity is not judged by their own
 * profile. Resolve it once per wizard and hand it to the steps that need it.
 */
export function useWizardInitialHub(initialHubId: string | null | undefined): WizardInitialHub {
  const { data: user } = useCurrentUser();
  const exempt = !!user?.isAdmin;
  const { data: profile } = useOwnProfessionalProfile({ enabled: !!user?.isProfessional && !exempt });
  const viewer = useHubViewer(initialHubId, { enabled: !!user });
  const { data: hub } = useHub(initialHubId ?? undefined);
  const blocked = viewer.data?.blocked;
  const standing = placementStanding({ profileStatus: profile?.status, blocked, exempt });
  const profileNotApproved = standing === 'underReview' || standing === 'notApproved';
  const initialHub = useMemo(
    () => (standing === 'offer' ? toInitialHub(hub, blocked) : null),
    [standing, blocked, hub]
  );
  const notice: HubPlacementNotice | null =
    initialHubId && profileNotApproved ? 'professional_not_approved' : standing === 'blocked' ? 'hub_blocked' : null;
  return { initialHub, notice, profileNotApproved };
}

/**
 * The Hub search a new venue picks its Hub from in the wizard's location step: none while the profile
 * is not approved, or when no Hub exists and the wizard was not opened at one.
 */
export function useWizardHubPlacement({ initialHub, profileNotApproved }: WizardInitialHub) {
  const [query, setQuery] = useState('');
  const results = useHubSearch(query);
  // The first page of Hubs, whatever is typed: no Hub at all means no "at a Hub" choice.
  const anyHub = useHubSearch('');
  const hubSearch: HubSearch | undefined =
    !profileNotApproved && (anyHub.hubs.length > 0 || initialHub)
      ? {
          query,
          onQueryChange: setQuery,
          results: results.hubs,
          isSearching: results.isSearching,
          isError: results.isError
        }
      : undefined;
  return { hubSearch, clearQuery: () => setQuery('') };
}
